/**
 * Survey session CRUD — start, pause, resume, complete.
 *
 * Follows the HP service pattern: invokeFunction → type-guard → typed result.
 * All writes are duplicated to the offline surveyStore so captures survive
 * connectivity gaps.
 */

import { invokeFunction } from '../api'
import { toApiRequestError } from '../errors'
import { safeLogger } from '../logger'

// ── Domain constants ────────────────────────────────────────────────────────

export const SURVEY_DOMAINS = [
  'IC',    // Infection Control
  'SPD',   // Sterile Processing
  'ENV',   // Environment of Care
  'LSC',   // Life Safety Code
  'MED',   // Medication Management
  'COMP',  // Compliance
  'WT',    // Waived Testing
  'CR',    // Clinical Records
  'HR',    // Human Resources
  'EM',    // Emergency Management
  'ADA',   // ADA Accessibility
  'WASTE', // Medical Waste Management
] as const

export type SurveyDomain = (typeof SURVEY_DOMAINS)[number]

export const SEVERITY_LEVELS = [1, 2, 3] as const
export type SeverityLevel = (typeof SEVERITY_LEVELS)[number]

export function severityLabel(level: SeverityLevel): 'Low' | 'Moderate' | 'High' {
  if (level === 1) return 'Low'
  if (level === 2) return 'Moderate'
  return 'High'
}

export function severityDescription(level: SeverityLevel): string {
  if (level === 1) return 'Isolated occurrence, limited impact, no direct patient harm'
  if (level === 2) return 'Pattern of noncompliance, potential for harm, systemic issue'
  return 'Immediate jeopardy, widespread noncompliance, actual harm or likelihood of serious injury/death'
}

export const FACILITY_AREAS = [
  'OR 1', 'OR 2', 'OR 3', 'OR 4',
  'Pre-Op', 'PACU', 'SPD', 'Med Storage',
  'Mechanical', 'Corridor', 'Lobby', 'Restroom',
  'Clean Utility', 'Soiled Utility', 'Staff Lounge',
  'Admin Office', 'Exterior', 'Other',
] as const

export type FacilityArea = (typeof FACILITY_AREAS)[number]

// ── Types ───────────────────────────────────────────────────────────────────

export type SurveyStatus = 'active' | 'paused' | 'completed'

export interface SurveySession {
  survey_id: string
  visit_id: string
  facility_name: string
  framework: string
  status: SurveyStatus
  started_at: string
  paused_at: string | null
  completed_at: string | null
  areas_visited: FacilityArea[]
}

export interface SurveyFinding {
  finding_id: string
  survey_id: string
  visit_id: string
  domain: SurveyDomain
  standard_ref: string
  severity: SeverityLevel
  observation: string
  area: FacilityArea
  photo_ids: string[]
  audio_transcript: string | null
  created_at: string
}

export interface SurveyTracer {
  tracer_id: string
  survey_id: string
  visit_id: string
  process_name: string
  steps: TracerStep[]
  created_at: string
}

export interface TracerStep {
  step_id: string
  order: number
  observation: string
  standard_ref: string
  result: 'pass' | 'fail' | 'na'
  photo_ids: string[]
  interview_notes: string | null
  audio_transcript: string | null
}

export interface SurveyDocument {
  document_id: string
  survey_id: string
  visit_id: string
  doc_type: 'policy' | 'log' | 'record' | 'certificate' | 'other'
  label: string
  evidence_path: string | null
  area: FacilityArea | null
  created_at: string
}

export interface SurveyPhoto {
  photo_id: string
  survey_id: string
  visit_id: string
  area: FacilityArea
  caption: string | null
  evidence_path: string | null
  gps_lat: number | null
  gps_lng: number | null
  taken_at: string
}

export interface WalkthroughPromptEvidence {
  note: string
  photoIds: string[]
  severity?: 1 | 2 | 3
  photoAnnotations?: Record<string, Array<{
    id: string
    x: number
    y: number
    comment: string
    severity: SeverityLevel
    created_at: string
  }>>
}

export interface WalkthroughStepState {
  status: 'pending' | 'finding' | 'skipped' | 'clear'
  findingIds: string[]
  photoIds: string[]
  notes: string
  checklistResponses?: Record<string, 'yes' | 'no' | 'na'>
  promptEvidence?: Record<string, WalkthroughPromptEvidence>
}

export interface WalkthroughState {
  survey_id: string
  areaIdx: number
  sectionIdx: number
  itemIdx: number
  areaPhase?: 'briefing' | 'photos' | 'inspection'
  currentRoom?: string | null
  stepStates: Record<string, WalkthroughStepState>
  lastUpdated: string
}

// ── Type guards ─────────────────────────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function asSurveySession(value: unknown): SurveySession | null {
  if (!isRecord(value)) return null
  if (typeof value.survey_id !== 'string') return null
  if (typeof value.visit_id !== 'string') return null
  if (typeof value.status !== 'string') return null
  return {
    survey_id: value.survey_id,
    visit_id: value.visit_id,
    facility_name: String(value.facility_name ?? ''),
    framework: String(value.framework ?? ''),
    status: value.status as SurveyStatus,
    started_at: String(value.started_at ?? new Date().toISOString()),
    paused_at: typeof value.paused_at === 'string' ? value.paused_at : null,
    completed_at: typeof value.completed_at === 'string' ? value.completed_at : null,
    areas_visited: Array.isArray(value.areas_visited) ? value.areas_visited : [],
  }
}

// ── Service functions ───────────────────────────────────────────────────────

export async function startSurvey(visitId: string, framework: string): Promise<SurveySession> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'POST',
    path: `/visits/${visitId}/survey:start`,
    body: { framework },
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to start survey session')
  const session = asSurveySession(data)
  if (!session) throw new Error('Malformed survey session response')
  safeLogger.logEvent('survey_started', { route: `/visits/${visitId}/survey` })
  return session
}

export async function getSurvey(visitId: string, options: { preferCompleted?: boolean } = {}): Promise<SurveySession | null> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'GET',
    path: `/visits/${visitId}/survey${options.preferCompleted ? '?prefer=completed' : ''}`,
  })
  if (error) {
    if (error.status === 404) return null
    throw toApiRequestError(error, 'Failed to load survey session')
  }
  return data ? asSurveySession(data) : null
}

export async function pauseSurvey(visitId: string, surveyId: string): Promise<SurveySession> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'POST',
    path: `/visits/${visitId}/survey/${surveyId}:pause`,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to pause survey')
  const session = asSurveySession(data)
  if (!session) throw new Error('Malformed survey session response')
  return session
}

export async function resumeSurvey(visitId: string, surveyId: string): Promise<SurveySession> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'POST',
    path: `/visits/${visitId}/survey/${surveyId}:resume`,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to resume survey')
  const session = asSurveySession(data)
  if (!session) throw new Error('Malformed survey session response')
  return session
}

export async function completeSurvey(visitId: string, surveyId: string): Promise<SurveySession> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'POST',
    path: `/visits/${visitId}/survey/${surveyId}:complete`,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to complete survey')
  const session = asSurveySession(data)
  if (!session) throw new Error('Malformed survey session response')
  safeLogger.logEvent('survey_completed', { route: `/visits/${visitId}/survey` })
  return session
}

function asWalkthroughState(value: unknown): WalkthroughState | null {
  if (!isRecord(value)) return null
  if (typeof value.survey_id !== 'string') return null
  if (typeof value.areaIdx !== 'number') return null
  if (typeof value.sectionIdx !== 'number') return null
  if (typeof value.itemIdx !== 'number') return null
  if (!isRecord(value.stepStates)) return null
  return value as unknown as WalkthroughState
}

export async function getWalkthroughState(visitId: string, surveyId: string): Promise<WalkthroughState | null> {
  const { data, error } = await invokeFunction<{ walkthrough_state: unknown }>('survey', {
    method: 'GET',
    path: `/visits/${visitId}/survey/${surveyId}/walkthrough-state`,
  })
  if (error) {
    if (error.status === 404) return null
    throw toApiRequestError(error, 'Failed to load walkthrough state')
  }
  return data?.walkthrough_state ? asWalkthroughState(data.walkthrough_state) : null
}

export async function saveWalkthroughState(visitId: string, surveyId: string, state: WalkthroughState): Promise<WalkthroughState> {
  const { data, error } = await invokeFunction<{ walkthrough_state: unknown }>('survey', {
    method: 'PUT',
    path: `/visits/${visitId}/survey/${surveyId}/walkthrough-state`,
    body: { walkthrough_state: state },
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to save walkthrough state')
  const saved = asWalkthroughState(data.walkthrough_state)
  if (!saved) throw new Error('Malformed walkthrough state response')
  return saved
}

// ── Finding CRUD ────────────────────────────────────────────────────────────

export async function addFinding(
  visitId: string,
  surveyId: string,
  input: Omit<SurveyFinding, 'finding_id' | 'survey_id' | 'visit_id' | 'created_at'>,
): Promise<SurveyFinding> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'POST',
    path: `/visits/${visitId}/survey/${surveyId}/findings`,
    body: input,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to add finding')
  if (!isRecord(data) || typeof data.finding_id !== 'string') {
    throw new Error('Malformed finding response')
  }
  return data as unknown as SurveyFinding
}

export async function getFindings(visitId: string, surveyId: string): Promise<SurveyFinding[]> {
  const { data, error } = await invokeFunction<{ findings: unknown }>('survey', {
    method: 'GET',
    path: `/visits/${visitId}/survey/${surveyId}/findings`,
  })
  if (error) throw toApiRequestError(error, 'Failed to load findings')
  if (!data || !Array.isArray(data.findings)) throw new Error('Malformed findings response')
  return data.findings as SurveyFinding[]
}

// ── Tracer CRUD ─────────────────────────────────────────────────────────────

export async function addTracer(
  visitId: string,
  surveyId: string,
  input: { process_name: string },
): Promise<SurveyTracer> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'POST',
    path: `/visits/${visitId}/survey/${surveyId}/tracers`,
    body: input,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to add tracer')
  return data as unknown as SurveyTracer
}

export async function addTracerStep(
  visitId: string,
  surveyId: string,
  tracerId: string,
  input: Omit<TracerStep, 'step_id'>,
): Promise<TracerStep> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'POST',
    path: `/visits/${visitId}/survey/${surveyId}/tracers/${tracerId}/steps`,
    body: input,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to add tracer step')
  return data as unknown as TracerStep
}

// ── Document CRUD ───────────────────────────────────────────────────────────

export async function addDocument(
  visitId: string,
  surveyId: string,
  input: Omit<SurveyDocument, 'document_id' | 'survey_id' | 'visit_id' | 'created_at'>,
): Promise<SurveyDocument> {
  const { data, error } = await invokeFunction<unknown>('survey', {
    method: 'POST',
    path: `/visits/${visitId}/survey/${surveyId}/documents`,
    body: input,
  })
  if (error || !data) throw toApiRequestError(error, 'Failed to add document')
  return data as unknown as SurveyDocument
}
